import React from 'react';
import styled from 'styled-components/macro';
import * as Yup from 'yup';
import { Formik, Field as FormikField, FormikHelpers } from 'formik';
import { Typography, Button, Checkbox } from '@deeptrue-frontend/ui';
import intl, { useFormatMessage } from 'components/IntlProvider';
import { FormatMessage } from 'components/IntlProvider/hooks/hooks';
import api from 'api/api';
import StoreProvider from 'components/StoreProvider/StoreProvider';
import { translateFieldErrors } from 'components/IntlProvider/translateFieldErrors';
import { WithProjectProps } from '../withProject';

const { connect, actions } = StoreProvider;

const Header = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.colors.borders};
  height: 5.5rem;
  position: absolute;
  top: 0;
  z-index: -1;
  left: 0;
  padding: 1.5rem;
  width: 100%;
`;

const Content = styled.div`
  margin-top: 6.5rem;
  padding: 0 1.5rem;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  height: 5.5rem;
  border-top: 1px solid ${({ theme }) => theme.colors.borders};
  align-items: center;
  padding: 0 1.5rem;
  margin-top: 1rem;
`;

const CheckboxWrapper = styled.div`
  background-color: #fafafa;
  padding: 0.5rem;
`;

const Label = styled(Typography)<{ isError?: boolean }>`
  ${({ isError }) =>
    isError &&
    `
    color: #DC3545
  `}
`;

interface FormValues {
  terms: boolean;
  terms_agreement: boolean;
  privacy_policy_accepted: boolean;
}

const getValidationSchema = (t: FormatMessage) =>
  Yup.object().shape<FormValues>({
    terms: Yup.boolean().oneOf(
      [true],
      t('errors.fields.required', 'Field is required')
    ),
    terms_agreement: Yup.boolean().oneOf(
      [true],
      t('errors.fields.required', 'Field is required')
    ),
    privacy_policy_accepted: Yup.boolean().oneOf(
      [true],
      t('errors.fields.required', 'Field is required')
    ),
  });

interface Props {
  closeModal: () => void;
  initialValues: {
    terms: boolean;
    terms_agreement: boolean;
    privacy_policy_accepted: boolean;
  };
  projectId: number;
  organization: string;
  updateProject: (project: WithProjectProps['project']) => void;
  surveyUuid?: string;
}

const BexioStartModal = ({
  closeModal,
  initialValues,
  projectId,
  organization,
  updateProject,
  surveyUuid,
}: Props) => {
  const t = useFormatMessage();
  const { useIntl } = intl;
  const { locale } = useIntl();

  const submit = (
    val: FormValues,
    { setErrors }: FormikHelpers<FormValues>
  ) => {
    return api.resources
      .collectProjectData({
        id: projectId,
        organizationCodename: organization,
        val: val,
      })
      .then(({ data }) => {
        updateProject({ ...data, surveyUuid: surveyUuid });
        closeModal();
      })
      .catch((err: any) => {
        setErrors(
          translateFieldErrors({
            t,
            err: err.response.data,
          })
        );
      });
  };

  return (
    <Formik
      onSubmit={submit}
      initialValues={initialValues}
      validationSchema={getValidationSchema(t)}
    >
      {({
        handleSubmit,
        errors,
        touched,
        isSubmitting,
        values,
        setFieldValue,
      }) => (
        <>
          <Header>
            <Typography variant="h6">
              {t('project.testEdit.startCollecting', 'Start Data Collection')}
            </Typography>
            <Typography variant="copy1">
              {t(
                'project.testEdit.bexio.subtitle',
                "Let's collect customers feedback"
              )}
            </Typography>
          </Header>
          <Content>
            <Typography variant="copy1" mt="1rem" mb="1rem">
              {t(
                'project.testEdit.bexio.description',
                'Deeptrue will collect the data from the completed surveys. You will find results in report section, you can track the results on progress tab.'
              )}
            </Typography>
            <CheckboxWrapper>
              <FormikField
                name="terms"
                checked={values.terms}
                onChange={() => {
                  setFieldValue('terms', !values.terms);
                }}
                as={Checkbox}
                hasError={touched.terms && errors.terms}
                label={
                  <Label
                    variant="copy1"
                    isError={!!(touched.terms && errors.terms)}
                  >
                    {t(
                      'project.testEdit.bexio.terms',
                      ' I understand once I start the data collection cannot edit the Brief.'
                    )}
                  </Label>
                }
              />
            </CheckboxWrapper>
            <CheckboxWrapper>
              <FormikField
                name="terms_agreement"
                checked={values.terms}
                onChange={() => {
                  setFieldValue('terms_agreement', !values.terms_agreement);
                }}
                as={Checkbox}
                hasError={touched.terms_agreement && errors.terms_agreement}
                label={
                  <Label
                    variant="copy1"
                    isError={
                      !!(touched.terms_agreement && errors.terms_agreement)
                    }
                  >
                    {t(
                      'project.bexio.termCondition',
                      'When i click on `{button}`, i agree to deeptrue {terms}',
                      {
                        terms: (
                          <a
                            href={`https://deeptrue.com/${locale}/terms-and-conditions`}
                          >
                            {t('forms.signin.agreements2', 'Terms of Service')}
                          </a>
                        ),
                        button: t(
                          'project.testEdit.startCollectingData',
                          'Start Collecting Data'
                        ),
                      }
                    )}
                  </Label>
                }
              />
            </CheckboxWrapper>
            <CheckboxWrapper>
              <FormikField
                name="privacy_policy_accepted"
                checked={values.terms}
                onChange={() => {
                  setFieldValue(
                    'privacy_policy_accepted',
                    !values.privacy_policy_accepted
                  );
                }}
                as={Checkbox}
                hasError={
                  touched.privacy_policy_accepted &&
                  errors.privacy_policy_accepted
                }
                label={
                  <Label
                    variant="copy1"
                    isError={
                      !!(
                        touched.privacy_policy_accepted &&
                        errors.privacy_policy_accepted
                      )
                    }
                  >
                    {t(
                      'project.bexio.termCondition',
                      'When i click on `{button}`, i agree to deeptrue {terms}',
                      {
                        terms: (
                          <a
                            href={`https://deeptrue.com/${locale}/privacy-policy`}
                          >
                            {t('forms.signin.agreements4', 'Privacy Policy')}
                          </a>
                        ),
                        button: t(
                          'project.testEdit.startCollectingData',
                          'Start Collecting Data'
                        ),
                      }
                    )}
                  </Label>
                }
              />
            </CheckboxWrapper>
          </Content>
          <Footer>
            <Button variant="outlined" onClick={closeModal}>
              {t('pages.project.modal.cancel', 'Cancel')}
            </Button>
            <Button
              id="trigger-data-collection"
              ml="1rem"
              isLoading={isSubmitting}
              onClick={() => handleSubmit()}
              type="submit"
              variant="primary"
              style={{ whiteSpace: 'nowrap' }}
            >
              {t(
                'project.testEdit.startCollectingData',
                'Start Collecting Data'
              )}
            </Button>
          </Footer>
        </>
      )}
    </Formik>
  );
};

export default connect(null, {
  updateProject: actions.projects.setProject,
})(BexioStartModal);
